import { SyntheticEvent, useMemo } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { IManager, LeadDto } from '@sr/dto';
import { NotificationsConfig, useSnack } from 'shared/ui/snack';
import { useUpdateLeadManager } from './lib';
import { useManagersDropdownList } from '../../user/user-picker';
import {
  filterHiddenManagers,
  isToggleHiddenOption,
  renderManagerOption,
  splitToggleOptions,
  useHiddenManagerToggleState,
  withHiddenToggle
} from 'shared/ui/fired-manager-toggle';

type Props = {
  lead?: LeadDto;
};

const notificationConfig: NotificationsConfig = {
  itemTitleForm1: 'закрепленный менеджер',
  itemTitleForm2: 'закрепленного менеджера',
  gender: 'male'
};

export const LeadManagerChanger = ({ lead }: Props) => {
  const { showSuccessUpdate, showFailedUpdate } = useSnack(notificationConfig);
  const { update, isUpdating } = useUpdateLeadManager();
  const managers = useManagersDropdownList();

  const nonSelectOption: IManager = useMemo(
    () => ({ firstName: '-', secondName: '', id: 0 }) as IManager,
    []
  );

  const allManagers: IManager[] = useMemo(
    () => [nonSelectOption, ...managers.items],
    [nonSelectOption, managers.items]
  );

  const { showHidden, toggleShowHidden, hasHiddenManagers } =
    useHiddenManagerToggleState(allManagers);

  const options = useMemo(
    () =>
      withHiddenToggle(filterHiddenManagers(allManagers, showHidden), {
        hasHiddenManagers
      }),
    [allManagers, showHidden, hasHiddenManagers]
  );

  const handleManagerChange = (_event: SyntheticEvent, value: IManager) => {
    if (!lead) return;

    if (isToggleHiddenOption(value)) {
      toggleShowHidden();
      return;
    }

    if (value === null) return;

    update({ leadId: lead.id, managerId: value.id ? value.id : null })
      .unwrap()
      .then(showSuccessUpdate)
      .catch((e) => showFailedUpdate(e));
  };

  const val = lead?.manager || nonSelectOption;

  return (
    <Autocomplete
      fullWidth
      value={val}
      options={options}
      onChange={handleManagerChange}
      disableClearable={true}
      isOptionEqualToValue={(option, value) =>
        option.id === value.id ||
        (isToggleHiddenOption(option) && isToggleHiddenOption(value))
      }
      getOptionLabel={(option) => {
        if (isToggleHiddenOption(option)) return '';
        return `${option.firstName} ${option.secondName}`;
      }}
      filterOptions={splitToggleOptions}
      loadingText={'Загрузка...'}
      loading={isUpdating || managers.isLoading}
      noOptionsText={'Ничего не найдено'}
      renderOption={(optionProps, option) =>
        renderManagerOption(optionProps, option, {
          showHidden,
          onToggle: toggleShowHidden
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={'Менеджер'}
          placeholder="Начните набирать имя..."
        />
      )}
    />
  );
};